import { useState, type ReactNode } from "react";
import { Alert, Box, Column, SectionHeading } from "@hightouch/htui";
import * as Sentry from "src/lib/monitoring";
import { LineChartDataSchema } from "@hightouch/lib/llm/schema/components/schemas";
import { AnalyticsGraph } from "src/components/charts/engine/analytics-graph";
import { GraphType } from "@hightouch/lib/query/visual/types";
import { transformChartToGraphSeries } from "./chart-transformer";
import { ChartWithAxisLabels } from "./chart-axis-labels";
import { Card } from "src/components/card";
import {
  COMPACT_CHART_HEIGHT,
  DEFAULT_CHART_HEIGHT,
  parseJsonChildren,
} from "./chart-utils";

/**
 * Line chart component for displaying time-series performance data
 * from LLM agents.
 *
 * Data is passed as children content (JSON string).
 */
export const LineChart = ({
  children,
  compact = false,
}: {
  children: ReactNode;
  compact?: boolean;
}) => {
  const [hoveredLine, setHoveredLine] = useState<string | undefined>();

  try {
    const chartData = parseJsonChildren(children, LineChartDataSchema);
    const graphSeries = transformChartToGraphSeries(chartData);

    // Extract categorical labels from the first series
    const categoricalLabels =
      chartData.series[0]?.data.map((d) => d.label) ?? [];

    const graph = (
      <ChartWithAxisLabels
        xAxisLabel={chartData.config.xAxisLabel}
        yAxisLabel={chartData.config.yAxisLabel}
      >
        <AnalyticsGraph
          type={GraphType.Line}
          graphSeries={graphSeries}
          categoricalLabels={categoricalLabels}
          hoveredLine={hoveredLine}
          onHoverLine={setHoveredLine}
          height={compact ? COMPACT_CHART_HEIGHT : DEFAULT_CHART_HEIGHT}
        />
      </ChartWithAxisLabels>
    );

    if (compact) {
      return <Box py={2}>{graph}</Box>;
    }

    return (
      <Card my={4} p={4}>
        <Column gap={2}>
          <SectionHeading>{chartData.config.title}</SectionHeading>
          {graph}
        </Column>
      </Card>
    );
  } catch (error) {
    Sentry.captureException(error);
    return (
      <Alert
        type="error"
        title="Chart Error"
        message="Unable to render line chart, invalid data."
        my={4}
      />
    );
  }
};
